import { Device, Vector2, degreesToRadians } from "@app/shared";
import { useContext, useEffect, useRef } from "react";
import { Vector } from "ts-matrix";
import { MapContext, MapObjArray } from "../../../../helpers";
import { ICoordsHelper, useFacilityCoordsHelper } from "../../../../helpers/coordsHelpers";
import { MapShapeProps } from "../MapShape";

// Screen-pixel distance from the device dot to the heading arrow. Kept constant across zoom
// levels so the arrow never visually overlaps the dot — see issue #669.
const ARROW_PIXEL_DISTANCE = 32;

function getArrowPosition(devicePos: Vector2, headingDegrees: number, distance: number = 3): Vector2 {
    const angleRad = degreesToRadians(headingDegrees);
    const offsetX = Math.sin(angleRad) * distance;
    const offsetY = -Math.cos(angleRad) * distance;

    return new Vector2({
        x: devicePos.x + offsetX,
        y: devicePos.y + offsetY,
    });
}

// Computes the arrow's LatLng so it sits at a fixed pixel distance from the device on screen,
// regardless of zoom. Falls back to a 3 m meters-based offset if the map projection isn't
// available yet (e.g., during the very first render before the map is fully initialized);
// the arrow gets repositioned correctly on the next zoom_changed event.
function getArrowLatLng(
    map: google.maps.Map | undefined,
    coordsHelper: ICoordsHelper,
    devicePos: Vector2,
    headingDegrees: number,
): google.maps.LatLng {
    const projection = map?.getProjection();
    if (projection && map) {
        const zoom = map.getZoom() ?? 0;
        const scale = Math.pow(2, zoom);
        const deviceLatLng = coordsHelper.toLatLng(devicePos);
        const devicePoint = projection.fromLatLngToPoint(deviceLatLng);
        if (devicePoint) {
            const angleRad = degreesToRadians(headingDegrees);
            const offsetX = (Math.sin(angleRad) * ARROW_PIXEL_DISTANCE) / scale;
            const offsetY = (-Math.cos(angleRad) * ARROW_PIXEL_DISTANCE) / scale;
            const arrowPoint = new google.maps.Point(devicePoint.x + offsetX, devicePoint.y + offsetY);
            const result = projection.fromPointToLatLng(arrowPoint);
            if (result) return result;
        }
    }
    return coordsHelper.toLatLng(getArrowPosition(devicePos, headingDegrees));
}

function calculateHeadingFromPositions(devicePos: Vector2, arrowPos: Vector2): number {
    const deltaX = arrowPos.x - devicePos.x;
    const deltaY = arrowPos.y - devicePos.y;

    let heading = (Math.atan2(deltaX, -deltaY) * 180) / Math.PI;

    if (heading < 0) {
        heading += 360;
    }

    return heading;
}

function calculateBoresightGroundIntersection(device: Device): Vector2 {
    const devicePos = new Vector([device.positionX ?? 0, device.positionY ?? 0, device.height ?? 0]);

    // Calculate boresight direction (center of FOV)
    const headingRad = degreesToRadians(device.headingDegrees ?? 0);
    const elevationRad = degreesToRadians(-(device.desiredTiltDegrees ?? 0));

    // Boresight direction vector
    const boresightDirection = new Vector([Math.sin(headingRad) * Math.cos(elevationRad), -Math.cos(headingRad) * Math.cos(elevationRad), -Math.sin(elevationRad)]);

    // Calculate ground intersection using the existing function
    const groundIntersection = getGroundIntersection(devicePos, boresightDirection);

    if (!groundIntersection) {
        const fallbackDistance = 5;
        return new Vector2({
            x: (device.positionX ?? 0) + Math.sin(headingRad) * fallbackDistance,
            y: (device.positionY ?? 0) - Math.cos(headingRad) * fallbackDistance,
        });
    }

    return new Vector2({ x: groundIntersection.at(0), y: groundIntersection.at(1) });
}

export const DeviceMapShape: React.FC<MapShapeProps<Device>> = ({ facility, shape, isSelected, isEditable, isSelectable, onUpdate, onClick }) => {
    const [parentMap, setBounds] = useContext(MapContext);
    const mapObjects = useRef([] as MapObjArray);
    const deviceMarker = useRef<google.maps.marker.AdvancedMarkerElement>();
    const headingArrow = useRef<google.maps.marker.AdvancedMarkerElement>();
    const boresightCrosshair = useRef<google.maps.marker.AdvancedMarkerElement>();
    const fov = useRef<google.maps.Polygon>();
    const fCoordsHelper = useFacilityCoordsHelper(facility);

    // Create markers once when component mounts or shape ID changes
    useEffect(() => {
        if (!deviceMarker.current && parentMap && typeof shape?.positionX === "number") {
            try {
                const newPolys = initPolys(shape, parentMap, fCoordsHelper);
                if (newPolys) {
                    deviceMarker.current = newPolys.marker;
                    headingArrow.current = newPolys.arrow;
                    boresightCrosshair.current = newPolys.boresight;
                    fov.current = newPolys.fov;
                    mapObjects.current = [deviceMarker.current!, headingArrow.current!, boresightCrosshair.current!, fov.current!];
                    setBounds((curr) => {
                        fov.current
                            ?.getPath()
                            ?.getArray()
                            ?.forEach((pt) => curr.extend(pt));
                        return curr;
                    });
                }
            } catch (error) {
                console.error("Error initializing device map shape:", error);
            }
        }
        return () => {
            mapObjects.current?.forEach((mo) => {
                if ("setVisible" in mo) {
                    mo.setVisible(false);
                    mo.setMap(null);
                } else {
                    (mo as google.maps.marker.AdvancedMarkerElement).map = null;
                }
            });
            deviceMarker.current = undefined;
            headingArrow.current = undefined;
            boresightCrosshair.current = undefined;
            mapObjects.current = [];
        };
    }, [parentMap, shape?.id]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (deviceMarker.current && headingArrow.current && boresightCrosshair.current && fov.current && shape) {
            try {
                updatePolys(deviceMarker.current, headingArrow.current, boresightCrosshair.current, fov.current, shape, parentMap, fCoordsHelper);
            } catch (error) {
                console.error("Error updating device map shape:", error);
            }
        }
    }, [shape, parentMap, fCoordsHelper]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (deviceMarker.current) {
            deviceMarker.current.gmpDraggable = isEditable ?? false;
        }
    }, [isEditable]);

    // Reposition the heading arrow on zoom so it stays a fixed pixel distance from the dot
    // and never visually covers it (issue #669).
    useEffect(() => {
        if (!parentMap || !shape || typeof shape.positionX !== "number") return;

        const reposition = () => {
            if (!headingArrow.current) return;
            const devicePos = new Vector2({ x: shape.positionX, y: shape.positionY });
            headingArrow.current.position = getArrowLatLng(parentMap, fCoordsHelper, devicePos, shape.headingDegrees ?? 0);
        };

        const zoomListener = parentMap.addListener("zoom_changed", reposition);
        const projectionListener = parentMap.addListener("projection_changed", reposition);
        // Run once now in case the projection just became available.
        reposition();

        return () => {
            zoomListener.remove();
            projectionListener.remove();
        };
    }, [parentMap, shape, fCoordsHelper]);

    useEffect(() => {
        const dv = deviceMarker.current;
        let dvDragListener = null as null | google.maps.MapsEventListener,
            dvDragEndListener = null as null | google.maps.MapsEventListener,
            dvDragStartListener = null as null | google.maps.MapsEventListener;

        if (!dv || !isEditable) {
            return;
        }

        dvDragStartListener = dv.addListener("dragstart", () => {
            if (dv.content instanceof HTMLElement) {
                dv.content.style.cursor = "grabbing";
            }
        });

        dvDragListener = dv.addListener("drag", (d: google.maps.MapMouseEvent) => {
            if (d.latLng) {
                dv.position = d.latLng;
                const offset = fCoordsHelper.fromLatLng(d.latLng);
                const updatedDev = Object.assign(new Device(), { ...shape, positionX: offset.x, positionY: offset.y });
                fov.current?.setPath(createFoV(updatedDev, undefined, fCoordsHelper).getPath());

                if (headingArrow.current) {
                    const arrowLatLng = getArrowLatLng(parentMap, fCoordsHelper, new Vector2({ x: offset.x, y: offset.y }), updatedDev.headingDegrees);
                    headingArrow.current.position = arrowLatLng;
                }

                if (boresightCrosshair.current) {
                    const boresightPos = calculateBoresightGroundIntersection(updatedDev);
                    const boresightLatLng = fCoordsHelper.toLatLng(boresightPos);
                    boresightCrosshair.current.position = boresightLatLng;
                }
            }
        });

        dvDragEndListener = dv.addListener("dragend", (d: google.maps.MapMouseEvent) => {
            if (dv.content instanceof HTMLElement) {
                dv.content.style.cursor = "pointer";
            }
            if (d.latLng) {
                const offset = fCoordsHelper.fromLatLng(d.latLng);
                const updatedDev = Object.assign(new Device(), { ...shape, positionX: offset.x, positionY: offset.y });
                onUpdate?.(updatedDev);
            }
        });

        return () => {
            dvDragStartListener?.remove();
            dvDragListener?.remove();
            dvDragEndListener?.remove();
        };
    }, [isEditable, shape, parentMap, deviceMarker]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const arrow = headingArrow.current;
        if (!arrow || !isEditable || !shape || !parentMap) {
            return;
        }

        let isDragging = false;
        let mapMouseMoveListener: google.maps.MapsEventListener | null = null;

        const arrowElement = arrow.content as HTMLElement;
        if (!arrowElement) return;

        const handleArrowMouseDown = (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            isDragging = true;

            arrowElement.style.cursor = "grabbing";

            mapMouseMoveListener = parentMap.addListener("mousemove", handleMapMouseMove);
            document.addEventListener("mouseup", handleDocumentMouseUp);
        };

        const handleMapMouseMove = (e: google.maps.MapMouseEvent) => {
            if (!isDragging || !e.latLng || !deviceMarker.current) return;

            const devicePos = new Vector2({ x: shape.positionX, y: shape.positionY });
            const mouseOffset = fCoordsHelper.fromLatLng(e.latLng);

            // Calculate new heading based on mouse position
            const newHeading = calculateHeadingFromPositions(devicePos, mouseOffset);

            // Constrain arrow to fixed pixel distance from device (issue #669)
            arrow.position = getArrowLatLng(parentMap, fCoordsHelper, devicePos, newHeading);

            if (arrow.content instanceof HTMLElement) {
                const svg = arrow.content.querySelector("svg g");
                if (svg) {
                    svg.setAttribute("transform", `rotate(${newHeading} 12 12)`);
                }
            }

            // Update FOV in real-time
            const updatedDev = Object.assign(new Device(), { ...shape, headingDegrees: newHeading });
            fov.current?.setPath(createFoV(updatedDev, undefined, fCoordsHelper).getPath());

            // Update boresight crosshair
            if (boresightCrosshair.current) {
                const boresightPos = calculateBoresightGroundIntersection(updatedDev);
                const boresightLatLng = fCoordsHelper.toLatLng(boresightPos);
                boresightCrosshair.current.position = boresightLatLng;
            }
        };

        const handleDocumentMouseUp = (e: MouseEvent) => {
            if (!isDragging) return;

            isDragging = false;
            arrowElement.style.cursor = "grab";

            const mapDiv = parentMap.getDiv();
            const rect = mapDiv.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            const projection = parentMap.getProjection();
            if (projection) {
                const bounds = parentMap.getBounds();
                if (bounds) {
                    const ne = bounds.getNorthEast();
                    const sw = bounds.getSouthWest();
                    const topRight = projection.fromLatLngToPoint(ne);
                    const bottomLeft = projection.fromLatLngToPoint(sw);

                    if (topRight && bottomLeft) {
                        const scale = Math.pow(2, parentMap.getZoom() ?? 0);
                        const worldPoint = new google.maps.Point(x / scale + bottomLeft.x, y / scale + topRight.y);
                        const latLng = projection.fromPointToLatLng(worldPoint);

                        if (latLng) {
                            const devicePos = new Vector2({ x: shape.positionX, y: shape.positionY });
                            const mouseOffset = fCoordsHelper.fromLatLng(latLng);

                            // Calculate final heading
                            const newHeading = calculateHeadingFromPositions(devicePos, mouseOffset);

                            // Ensure arrow is at correct pixel distance from device (issue #669)
                            arrow.position = getArrowLatLng(parentMap, fCoordsHelper, devicePos, newHeading);

                            // Update device with new heading
                            const updatedDev = Object.assign(new Device(), { ...shape, headingDegrees: newHeading });
                            onUpdate?.(updatedDev);
                        }
                    }
                }
            }

            // Remove listeners
            if (mapMouseMoveListener) {
                mapMouseMoveListener.remove();
                mapMouseMoveListener = null;
            }
            document.removeEventListener("mouseup", handleDocumentMouseUp);
        };

        arrowElement.addEventListener("mousedown", handleArrowMouseDown);

        return () => {
            arrowElement.removeEventListener("mousedown", handleArrowMouseDown);
            document.removeEventListener("mouseup", handleDocumentMouseUp);

            if (mapMouseMoveListener) {
                mapMouseMoveListener.remove();
            }
        };
    }, [isEditable, shape, parentMap, headingArrow, fCoordsHelper, onUpdate]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        // handle selection from list
        if (!isSelected || !deviceMarker.current || !fov.current) {
            return;
        }
        animateSelect(fov.current, deviceMarker.current, parentMap);
    }, [isSelected, parentMap, deviceMarker]);

    useEffect(() => {
        //selection from map
        if (!isSelectable) {
            return;
        }
        let click: google.maps.MapsEventListener;
        let clickFov: google.maps.MapsEventListener;
        let clickArrow: google.maps.MapsEventListener;
        let clickBoresight: google.maps.MapsEventListener;

        if (onClick && parentMap && deviceMarker.current && shape) {
            fov.current?.set("clickable", true);

            click = deviceMarker.current.addListener("click", (e: google.maps.MapMouseEvent) => onClick?.(e, shape));
            clickFov = fov.current!.addListener("click", (e: google.maps.MapMouseEvent) => onClick?.(e, shape));
            clickArrow = headingArrow.current!.addListener("click", (e: google.maps.MapMouseEvent) => onClick?.(e, shape));
            clickBoresight = boresightCrosshair.current!.addListener("click", (e: google.maps.MapMouseEvent) => onClick?.(e, shape));
        }
        return () => {
            fov.current?.set("clickable", false);
            click?.remove();
            clickFov?.remove();
            clickArrow?.remove();
            clickBoresight?.remove();
        };
    }, [onClick, parentMap, deviceMarker, headingArrow, shape, isSelectable]);

    return null;

    function initPolys(device: Device, parentMap: google.maps.Map, coordsHelper: ICoordsHelper): { marker: google.maps.marker.AdvancedMarkerElement; arrow: google.maps.marker.AdvancedMarkerElement; boresight: google.maps.marker.AdvancedMarkerElement; fov: google.maps.Polygon } | undefined {
        if (typeof device.positionX !== "number") {
            return undefined;
        }
        const devicePos = new Vector2({ x: device.positionX, y: device.positionY });
        const coords = coordsHelper.toLatLng(devicePos);

        const circleElement = document.createElement("div");
        circleElement.style.width = "10px";
        circleElement.style.height = "10px";
        circleElement.style.borderRadius = "50%";
        circleElement.style.backgroundColor = device.color ?? "blue";
        circleElement.style.border = `1px solid ${device.color ?? "blue"}`;
        circleElement.style.cursor = "pointer";

        const positionCircle = new google.maps.marker.AdvancedMarkerElement({
            position: coords,
            map: parentMap,
            content: circleElement,
            gmpDraggable: false,
            zIndex: 9,
        });

        // Create heading arrow at a fixed pixel distance from the device dot (issue #669)
        const headingDeg = device.headingDegrees ?? 0;
        const arrowCoords = getArrowLatLng(parentMap, coordsHelper, devicePos, headingDeg);

        const arrowElement = document.createElement("div");
        arrowElement.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <g transform="rotate(${headingDeg} 12 12)">
                    <path d="M12 2 L20 10 L16 10 L16 22 L8 22 L8 10 L4 10 Z"
                          fill="${device.color ?? "red"}"
                          stroke="white"
                          stroke-width="1"/>
                </g>
            </svg>
        `;
        arrowElement.style.cursor = "grab";

        const headingArrowMarker = new google.maps.marker.AdvancedMarkerElement({
            position: arrowCoords,
            map: parentMap,
            content: arrowElement,
            title: `Heading: ${headingDeg.toFixed(1)}°`,
            zIndex: 10,
            gmpDraggable: false, // We handle dragging manually to constrain position
        });

        // Create boresight crosshair
        const boresightPos = calculateBoresightGroundIntersection(device);
        const boresightCoords = coordsHelper.toLatLng(boresightPos);

        const crosshairElement = document.createElement("div");
        crosshairElement.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                <g>
                    <line x1="8" y1="2" x2="8" y2="6" stroke="white" stroke-width="2"/>
                    <line x1="8" y1="10" x2="8" y2="14" stroke="white" stroke-width="2"/>
                    <line x1="2" y1="8" x2="6" y2="8" stroke="white" stroke-width="2"/>
                    <line x1="10" y1="8" x2="14" y2="8" stroke="white" stroke-width="2"/>
                    <circle cx="8" cy="8" r="1" fill="white"/>
                </g>
            </svg>
        `;

        const boresightCrosshair = new google.maps.marker.AdvancedMarkerElement({
            position: boresightCoords,
            map: parentMap,
            content: crosshairElement,
            title: "Boresight",
            zIndex: 8,
        });

        const poly = createFoV(device, parentMap, coordsHelper);
        return { marker: positionCircle, arrow: headingArrowMarker, boresight: boresightCrosshair, fov: poly };
    }
};

function updatePolys(
    marker: google.maps.marker.AdvancedMarkerElement,
    arrow: google.maps.marker.AdvancedMarkerElement,
    boresight: google.maps.marker.AdvancedMarkerElement,
    fov: google.maps.Polygon,
    shape: Device | undefined,
    parentMap: google.maps.Map | undefined,
    coordsHelper: ICoordsHelper
): { marker: google.maps.marker.AdvancedMarkerElement | undefined; arrow: google.maps.marker.AdvancedMarkerElement | undefined; boresight: google.maps.marker.AdvancedMarkerElement | undefined; fov: google.maps.Polygon | undefined } {
    marker.map = parentMap ?? null;
    arrow.map = parentMap ?? null;
    boresight.map = parentMap ?? null;
    fov.setMap(parentMap ?? null);

    if (!parentMap || !shape) {
        return { marker: undefined, arrow: undefined, boresight: undefined, fov: undefined };
    }

    const devicePos = new Vector2({ x: shape.positionX, y: shape.positionY });
    const coords = coordsHelper.toLatLng(devicePos);

    marker.position = coords;

    if (marker.content instanceof HTMLElement) {
        const currentColor = shape.color ?? "blue";
        marker.content.style.backgroundColor = currentColor;
        marker.content.style.border = `1px solid ${currentColor}`;
    }

    const headingDeg = shape.headingDegrees ?? 0;
    arrow.position = getArrowLatLng(parentMap, coordsHelper, devicePos, headingDeg);

    if (arrow.content instanceof HTMLElement) {
        const svg = arrow.content.querySelector("svg g");
        if (svg) {
            svg.setAttribute("transform", `rotate(${headingDeg} 12 12)`);
        }
        const path = arrow.content.querySelector("path");
        if (path) {
            path.setAttribute("fill", shape.color ?? "red");
        }
    }
    arrow.title = `Heading: ${headingDeg.toFixed(1)}°`;

    // Update boresight crosshair position
    const boresightPos = calculateBoresightGroundIntersection(shape);
    const boresightCoords = coordsHelper.toLatLng(boresightPos);
    boresight.position = boresightCoords;

    const deviceHeight = shape.height ?? 0;
    const isHeightValid = deviceHeight >= 0.1;

    fov.setPath(createFoV(shape, undefined, coordsHelper).getPath());
    fov.setOptions({
        strokeColor: shape.color ?? "#5900ff",
        fillColor: shape.color ?? "#5900ff",
        strokeOpacity: isHeightValid ? 0.8 : 0,
        fillOpacity: isHeightValid ? 0.35 : 0,
    });

    return { marker, arrow, boresight, fov };
}

function createFoV(device: Device, parentMap: google.maps.Map | undefined, coordsHelper: ICoordsHelper) {
    const deviceHeight = device.height ?? 0;
    const deviceTilt = device.desiredTiltDegrees ?? 0;
    const deviceHeading = device.headingDegrees ?? 0;
    const posX = device.positionX ?? 0;
    const posY = device.positionY ?? 0;

    if (deviceHeight < 0.1) {
        const devicePos = new Vector2({ x: posX, y: posY });
        const singlePoint = coordsHelper.toLatLng(devicePos);
        return new google.maps.Polygon({
            paths: [singlePoint, singlePoint, singlePoint],
            strokeOpacity: 0,
            fillOpacity: 0,
            map: parentMap,
            clickable: false,
            zIndex: 5,
        });
    }

    const fovPath = getPathForFieldOfView(posX, posY, deviceHeight, deviceTilt, 90, 30, 1);
    const fovPoints = fovPath
        .split(",")
        .filter((x) => x.length > 1)
        .map((pt) => {
            pt = pt.substring(2);
            const spIdx = pt.indexOf(" ");
            return { lng: parseFloat(pt.substring(0, spIdx)), lat: parseFloat(pt.substring(spIdx + 1)) };
        })
        .filter((pt) => isFinite(pt.lng) && isFinite(pt.lat));

    if (fovPoints.length < 3) {
        const devicePos = new Vector2({ x: posX, y: posY });
        const singlePoint = coordsHelper.toLatLng(devicePos);
        return new google.maps.Polygon({
            paths: [singlePoint, singlePoint, singlePoint],
            strokeOpacity: 0,
            fillOpacity: 0,
            map: parentMap,
            clickable: false,
            zIndex: 5,
        });
    }

    const ptsToDraw = rotate(fovPoints, new Vector2({ x: posX, y: posY }), 180 + deviceHeading);
    const coordsToDraw = ptsToDraw
        .map((pt) => new Vector2({ y: pt.lat, x: pt.lng }))
        .map(coordsHelper.toLatLng)
        .filter((coord) => isFinite(coord.lat()) && isFinite(coord.lng()));

    if (coordsToDraw.length < 3) {
        const devicePos = new Vector2({ x: posX, y: posY });
        const singlePoint = coordsHelper.toLatLng(devicePos);
        return new google.maps.Polygon({
            paths: [singlePoint, singlePoint, singlePoint],
            strokeOpacity: 0,
            fillOpacity: 0,
            map: parentMap,
            clickable: false,
            zIndex: 5,
        });
    }

    // Construct the polygon.
    const poly = new google.maps.Polygon({
        paths: coordsToDraw,
        strokeColor: device.color ?? "#5900ff",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: device.color ?? "#5900ff",
        fillOpacity: 0.35,
        geodesic: false,
        map: parentMap,
        clickable: false,
        zIndex: 5,
    });
    return poly;
}

function getGroundIntersection(linePoint: Vector, lineDirection: Vector) {
    const planePoint = new Vector([1, 1, 0]);
    const planeNormal = new Vector([0, 0, 1]);

    const normalizedDirection = lineDirection.normalize();
    const denominator = planeNormal.dot(normalizedDirection);

    if (Math.abs(denominator) < 0.0001) {
        return null;
    }

    var t = (planeNormal.dot(planePoint) - planeNormal.dot(linePoint)) / denominator;

    if (!isFinite(t) || isNaN(t) || t < 0) {
        return null;
    }

    var distance = normalizedDirection.scale(t);
    return linePoint.add(distance);
}

function getPathForFieldOfView(originX: number, originY: number, height: number, elevationAngle: number, azimuthFov: number, elevationFov: number, scaleRatio: number) {
    const apexHeight = height;
    const maxLength = 75;

    const declinationAngle = degreesToRadians(90 - Math.abs(elevationAngle));
    const fovWidth = degreesToRadians(108);
    const fovHeight = degreesToRadians(40);

    const apex = new Vector([0, 0, apexHeight]);
    const segments = 30;
    const pointsToDraw: { x: number; y: number }[] = [];

    for (let i = 0; i < segments; i++) {
        const theta = (2 * Math.PI * i) / segments;
        let x = (fovWidth / 2) * Math.cos(theta);
        let y = (fovHeight / 2) * Math.sin(theta);

        if (declinationAngle + y > Math.PI / 2) {
            y = degreesToRadians(89) - declinationAngle;
        }

        const topRay = new Vector([Math.sin(x), Math.sin(declinationAngle + y), -Math.cos(declinationAngle + y)]);
        let top = getGroundIntersection(apex, topRay);

        if (!top) {
            const normalizedRay = topRay.normalize();
            top = apex.add(normalizedRay.scale(maxLength));
        }

        const distance = top.subtract(apex);
        if (distance.length() > maxLength) {
            top = top.scale(maxLength / distance.length());
        }

        pointsToDraw.push({ x: top.at(0) + originX, y: top.at(1) + originY });
    }

    const pathCommands = pointsToDraw.map((point, index) => {
        const command = index === 0 ? "M" : "L";
        return `${command} ${point.x} ${point.y}`;
    });

    pathCommands.push("Z");

    return pathCommands.join(",");
}

function rotate(points: { lng: number; lat: number }[], center: Vector2, headingDegrees: number): { lng: number; lat: number }[] {
    if (points.length < 1) return points;
    const rotated = points.map((latLng) => {
        var point = new Vector2({ x: latLng.lng, y: latLng.lat });
        var rotatedLatLng = rotatePoint(point, center, headingDegrees);
        return { lat: rotatedLatLng.y, lng: rotatedLatLng.x };
    });
    return rotated;
}

function rotatePoint(point: Vector2, center: Vector2, angle: number) {
    var angleRad = (angle * Math.PI) / 180.0;
    return {
        x: Math.cos(angleRad) * (point.x - center.x) - Math.sin(angleRad) * (point.y - center.y) + center.x,
        y: Math.sin(angleRad) * (point.x - center.x) + Math.cos(angleRad) * (point.y - center.y) + center.y,
    };
}

function animateSelect(line: google.maps.Polyline, point: google.maps.marker.AdvancedMarkerElement, map?: google.maps.Map) {
    const path = line.getPath();
    const pathArray = path?.getArray() ?? [];

    const vertexCircles = pathArray.map((pos) => {
        return getCircleForPosition(pos, map);
    });

    if (point.position) {
        vertexCircles.push(getCircleForPosition(point.position as google.maps.LatLng, map));
    }

    const pointMap = point.map;
    point.map = null;

    setTimeout(() => {
        vertexCircles.forEach((c) => {
            c.map = null;
        });
        point.map = pointMap;
    }, 1000);
}

function getCircleForPosition(pos: google.maps.LatLng, map: google.maps.Map | undefined): google.maps.marker.AdvancedMarkerElement {
    const circleElement = document.createElement("div");
    circleElement.style.width = "10px";
    circleElement.style.height = "10px";
    circleElement.style.borderRadius = "50%";
    circleElement.style.backgroundColor = "blue";
    circleElement.style.border = "1px solid blue";
    circleElement.style.pointerEvents = "none"; // Make it non-interactive

    return new google.maps.marker.AdvancedMarkerElement({
        position: pos,
        map: map,
        content: circleElement,
        gmpDraggable: false, // Explicitly make it non-draggable
    });
}
